import { unwrap, type Api, type Region } from "../api/index.js";
import type { Formatter } from "../format/index.js";

/**
 * Profile (W-2), headless. Read-only; always fetched fresh from
 * `GET /usersPrivate/profile` (the screen's query uses staleTime 0 and
 * gcTime 0). The server masks the ID number, so the full number never
 * reaches the browser. KYC wording differs per region: "Myinfo" is SG only.
 */

export interface ProfileData {
  isAuthenticated: boolean;
  name?: string;
  maskedId?: string;
  dob?: string;
  mobile?: string;
  address?: string;
}

export const profileQueryKeys = {
  all: ["profile"] as const,
  profile: () => ["profile", "view"] as const,
};

export interface ProfileApi {
  profile(): Promise<ProfileData>;
}

export function createProfileApi(api: Api): ProfileApi {
  const c = api.shared;
  return {
    async profile() {
      const r = await unwrap(c.GET("/api/sg/usersPrivate/profile", {}));
      return r.data;
    },
  };
}

export interface ProfileField {
  id: "name" | "id" | "dob" | "email" | "mobile" | "address";
  /** i18n key of the label. */
  labelKey: string;
  /** Display value; undefined → the screen shows "Not provided". */
  value?: string;
  /** References (the masked ID) are shown as returned, in monospace. */
  mono?: boolean;
}

export interface KycView {
  verified: boolean;
  /** Banner copy: verified (with Myinfo in SG) or unverified. */
  bannerTitleKey: string;
  bannerBodyKey: string;
  /** "Verify identity" shows only while unverified. */
  verifyTo?: string;
  /** Closing line: "update them in Myinfo or contact support" (SG) / support only (HK). */
  footerKey: string;
}

export interface ProfileView {
  kyc: KycView;
  fields: ProfileField[];
}

export const VERIFY_IDENTITY_ROUTE = "/account/verify";
/** Profile's "Verify identity" comes back to Profile (W-7). */
export const PROFILE_VERIFY_ROUTE = `${VERIFY_IDENTITY_ROUTE}?returnTo=${encodeURIComponent("/account/profile")}`;

export function kycView(region: Region, isAuthenticated: boolean | undefined): KycView {
  const verified = isAuthenticated === true;
  const sg = region === "sg";
  return {
    verified,
    bannerTitleKey: verified ? (sg ? "profile.kyc.verifiedTitleMyinfo" : "profile.kyc.verifiedTitle") : "profile.kyc.unverifiedTitle",
    bannerBodyKey: verified ? "profile.kyc.verifiedBody" : "profile.kyc.unverifiedBody",
    verifyTo: verified ? undefined : PROFILE_VERIFY_ROUTE,
    footerKey: sg ? "profile.footerMyinfo" : "profile.footer",
  };
}

export function buildProfileView(input: {
  region: Region;
  /** null = the profile is unknown (only the session fields are shown). */
  profile: ProfileData | null;
  /** The signed-in account: email always, name as the last fallback. */
  account: { name?: string; email?: string } | null;
  format: Formatter;
}): ProfileView {
  const p = input.profile;
  const dob = p?.dob ? input.format.date(p.dob) || p.dob : undefined;
  const mobile = p?.mobile ? input.format.phone(p.mobile) : undefined;
  return {
    kyc: kycView(input.region, p?.isAuthenticated),
    fields: [
      { id: "name", labelKey: "profile.fields.name", value: p?.name ?? input.account?.name },
      {
        id: "id",
        labelKey: input.region === "sg" ? "profile.fields.nric" : "profile.fields.idNumber",
        value: p?.maskedId,
        mono: true,
      },
      { id: "dob", labelKey: "profile.fields.dob", value: dob },
      { id: "email", labelKey: "profile.fields.email", value: input.account?.email },
      { id: "mobile", labelKey: "profile.fields.mobile", value: mobile },
      { id: "address", labelKey: "profile.fields.address", value: p?.address },
    ],
  };
}
